import { Link } from 'react-router-dom';
import { UserMenu } from '@/components/UserMenu';
import { Wordmark } from '@/components/Wordmark';
import { cn } from '@/lib/utils';

/**
 * Shared app-shell header for the signed-in workspace (Dashboard, Sent,
 * Settings). Replaces the inline `<header>` blocks each list page used to
 * carry — one source of truth for the top nav.
 *
 * Layout: Wordmark + tab-style nav on the left, user-menu avatar on the
 * right. Sticky + blurred bg matches the landing Nav so visitors moving
 * from public → app keep the same shell language.
 *
 * Full-screen workspaces (Editor / NewDraft) keep their bespoke headers
 * since they need page-specific controls (save, device toggle, etc.), but
 * they reuse `<UserMenu />` + an icon-only `<Wordmark />` so the brand and
 * account affordance stay consistent across the app.
 */

const TABS = [
  { id: 'drafts', label: 'Drafts', to: '/app' },
  { id: 'sent', label: 'Sent', to: '/app/sends' },
  { id: 'brand', label: 'Brand', to: '/app/brand' },
  { id: 'settings', label: 'Settings', to: '/app/settings' },
] as const;

export type AppHeaderTab = (typeof TABS)[number]['id'];

export function AppHeader({ currentTab }: { currentTab: AppHeaderTab }) {
  return (
    <header className="bg-background/85 border-border sticky top-0 z-30 border-b backdrop-blur">
      <div className="mx-auto flex h-14 max-w-[1200px] items-center justify-between gap-6 px-6 lg:px-10">
        <div className="flex items-center gap-6">
          <Wordmark to="/app" />

          <nav className="flex items-center gap-0.5">
            {TABS.map((tab) => {
              const active = tab.id === currentTab;
              return (
                <Link
                  key={tab.id}
                  to={tab.to}
                  className={cn(
                    'inline-flex h-8 items-center rounded-md px-3 text-sm transition-colors',
                    active
                      ? 'bg-muted text-foreground font-medium'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                  )}
                >
                  {tab.label}
                </Link>
              );
            })}
          </nav>
        </div>

        <UserMenu />
      </div>
    </header>
  );
}
